import {useEffect} from 'react';
import useLogin from '../../common/hook/useLogin';
import {getIsToday} from '../../common/util/date';
import {useFetchInfo} from '../../common/util/fetch';
import {
  getLocalStorageNumber,
  setLocalStorageNumber,
} from '../../common/util/storage';
import {sendEvent} from '../../common/util/tracking';
import {sendGtmEvent} from '../../common/util/tracking/gtm';
import {ActionType} from '../state/actions';
import {IViewType} from '../type';

export default function useViewerReadEvent({
  contentId,
  episodeId,
  contentTitle,
  viewType,
}: {
  contentId?: number;
  episodeId: number;
  contentTitle?: string;
  genre?: string;
  viewType: IViewType;
}) {
  const {isLogin, isLoginChecking} = useLogin();
  const {fetchStatus} = useFetchInfo(ActionType.FetchResources, episodeId);
  const {isFetched: isDataFetched} = useFetchInfo(
    ActionType.FetchViewerData,
    episodeId
  );

  useEffect(() => {
    if (
      isLoginChecking ||
      !contentId ||
      fetchStatus !== 'success' ||
      !isDataFetched
    )
      return;

    sendEvent({
      section: 'viewer',
      page: 'viewer',
      actionName: 'read',
      action: 'start',
      customProps: {
        page_content_id: contentId,
        page_episode_id: episodeId,
        viewer_type: viewType,
        is_first: isLogin ? (getIsTodayFirstRead() ? 'Y' : 'N') : undefined,
        toros_user_id_type: 'user_ex.user_id',
        toros_event_meta_id: contentId,
      },
    });
  }, [
    contentId,
    episodeId,
    fetchStatus,
    isDataFetched,
    isLogin,
    isLoginChecking,
    viewType,
  ]);

  useEffect(() => {
    if (isLoginChecking || !contentTitle || fetchStatus !== 'success') return;

    sendGtmEvent('CONTENT_VIEW', {content_name: contentTitle});
  }, [contentId, contentTitle, fetchStatus, isLogin, isLoginChecking]);
}

function getIsTodayFirstRead() {
  const now = Date.now();
  const lastReadTime = getLocalStorageNumber({key: LAST_READ_TIME_KEY});
  setLocalStorageNumber({key: LAST_READ_TIME_KEY, value: now});

  if (!lastReadTime) {
    return true;
  }

  const isTodayFirstRead = !getIsToday(lastReadTime);
  return isTodayFirstRead;
}

const LAST_READ_TIME_KEY = 'last-read-time';
